export type AlertType =
  | 'struggling-task'
  | 'completed-assessment'
  | 'completed-screener'
  | 'completed-diagnostic'
  | 'completed-unit'
  | 'mid-assessment-reminder'
  | 'post-assessment-reminder'
  | 'completed-objective'
  | 'weekly-usage-limit'
  | 'diagnostic-first'
  | 'poor-usage-time'
  | 'poor-usage-foil'
  | 'delinquent-login'
  | 'delinquent-diagnostic'
  | 'time-usage';

export type BaseNotificationData = {
  date: string;
  displayDate: string;
};

// NOTE: The wonky typing on these comes from the way dataservices historically
//     : has inserted Alerts and the XML tag-based data. It would be good in a future
//     : refactoring to clean this up so the alert data is properly cased, during a summer
export type StrugglingNotificationData = {
  firstname: string;
  lastname: string;
  unit: string;
  taskname: string;
  wordlist: string;
  score: string;
};

export type CompletedAssessmentNotificationData = {
  AssessmentType: string;
  SubscriptionType: string;
  FirstName: string;
  LastName: string;
  Date: string;
};

export type DelinquentDiagnosticNotificationData = {
  assessmentStartDate: string;
};

export type DelinquentLoginNotificationData = {
  lastLogin: string;
  status: string;
};

export type DiagnosticFirstLevelTimeAlertNotificationData = {
  firstname: string;
  lastname: string;
  sessiontime: string;
  assessmenttype: string;
  subscriptiontype: string;
};

export type AssessmentReminderNotificationData = {
  firstname: string;
  lastname: string;
};

export type ObjectiveCompletedNotificationData = {
  firstname: string;
  lastname: string;
  objective: string;
  testtype: string;
};

export type PoorUsageNotificationData = {
  firstname: string;
  lastname: string;
  poorUsageType: string;
};

export type UnitCompleteNotificationData = {
  firstname: string;
  lastname: string;
  unitcompleted: string;
};

export type WeeklyUsageLimitNotificationData = {
  firstname: string;
  lastname: string;
  weeklyusage: string;
};

export type TeacherNotificationData =
  & BaseNotificationData
  & StrugglingNotificationData
  & CompletedAssessmentNotificationData
  & DelinquentDiagnosticNotificationData
  & DelinquentLoginNotificationData
  & DiagnosticFirstLevelTimeAlertNotificationData
  & AssessmentReminderNotificationData
  & ObjectiveCompletedNotificationData
  & PoorUsageNotificationData
  & UnitCompleteNotificationData
  & WeeklyUsageLimitNotificationData
  & Record<string, unknown>;

export type TeacherNotification = {
  alertID: number;
  alertType: number;
  category: string;
  text: string;
  studentName: string;
  studentID: number;
  data: TeacherNotificationData;
  userDismissed: boolean;
};

export type TeacherNotificationResponse = {
  isErred: boolean;
  errorMsg?: string;
  data?: DateGroupedTeacherNotifications;
};

export type TeacherUsageNotificationResponse = {
  notificationId: number;
  teacherId: number;
  text: string;
  category: string;
  countsJson: string;
  startDate: number;
  endDate: number;
  dismissedAt: number;
  createdAt: number;
}

export type UsageBucket = {
  key: string;
  label: string;
  min: number | null;
  max: number | null;
  count: number;
  target: boolean;
};

export type TeacherUsageNotification = {
  notificationId: number;
  teacherId: number;
  text: string;
  category: string;
  buckets: UsageBucket[];
  dismissedAt: Date;
  createdAt: Date;
};

export type DisplayedTeacherNotification = {
  studentCount: number;
  category: string;
  text: string;
  type: string;
  displayDate: string;
  whereToLookCategory?: string;
  whereToLookLabel?: string;
  whereToLookRoute?: string;
}

export type DisplayedTeacherNotificationWhereToLook = {
  category: string;
  label: string;
  route: string;
}

export type NotificationPreamble = {
  text: string;
  isSubheading?: boolean;
}

export type NotificationStep = {
  title: string;
  body: string;
  variant?: 'green' | 'warn';
}

export type CategorizedTeacherNotifications = Partial<Record<AlertType, (TeacherNotification | TeacherUsageNotification)[]>>;
export type DateGroupedTeacherNotifications = Partial<Record<string, CategorizedTeacherNotifications>>;

export const WhereToLookMap: Record<AlertType, DisplayedTeacherNotificationWhereToLook> = {
  'struggling-task': {
    category: 'Reports: Progress Monitoring',
    label: 'Unit Detail Report',
    route: '/reports/system/unit-detail',
  },
  'completed-assessment': {
    category: 'Reports: Assessments',
    label: 'Diagnostic Results',
    route: '/reports/system/diagnostic',
  },
  'completed-screener': {
    category: 'Reports: Assessments',
    label: 'Screener Results',
    route: '/reports/screener',
  },
  'completed-diagnostic': {
    category: 'Reports: Assessments',
    label: 'Diagnostic Results',
    route: '/reports/system/diagnostic',
  },
  'completed-unit': {
    category: 'Reports: Progress Monitoring',
    label: 'Unit Detail',
    route: '/reports/system/unit-detail',
  },
  'mid-assessment-reminder': {
    category: 'Reports: Assessments',
    label: 'Diagnostic Results',
    route: '/reports/system/diagnostic',
  },
  'post-assessment-reminder': {
    category: 'Reports: Assessments',
    label: 'Diagnostic Results',
    route: '/reports/system/diagnostic',
  },
  'completed-objective': {
    category: 'Reports: Progress Monitoring',
    label: 'Objective Pre/Post',
    route: '/reports/system/objective',
  },
  'weekly-usage-limit': {
    category: 'Reports: Progress Monitoring',
    label: 'Group Summary',
    route: '/reports/system/summary/group',
  },
  'diagnostic-first': {
    category: 'Reports: Assessments',
    label: 'Diagnostic Results',
    route: '/reports/system/diagnostic',
  },
  'poor-usage-time': {
    category: 'Reports: Progress Monitoring',
    label: 'Unit Detail',
    route: '/reports/system/unit-detail',
  },
  'poor-usage-foil': {
    category: 'Reports: Progress Monitoring',
    label: 'Unit Detail',
    route: '/reports/system/unit-detail',
  },
  'delinquent-login': {
    category: 'Reports: Progress Monitoring',
    label: 'Group Summary',
    route: '/reports/system/summary/group',
  },
  'delinquent-diagnostic': {
    category: 'Reports: Assessments',
    label: 'Diagnostic Results',
    route: '/reports/system/diagnostic',
  },
  'time-usage': {
    category: 'Reports: Progress Monitoring',
    label: 'Group Summary',
    route: '/reports/system/summary/group',
  }
};
